import { FormStatus, P, Section, Table, Td, Th, Tr } from '@dnb/eufemia';
import { Form } from '@dnb/eufemia/extensions/forms';
import { ApiClassification, ApiType } from '@portals/shared/portal/ApiDto';

import { OrganizationVisibility, TeamVisibility } from '@/openapi/schema';
import { SwitchContainerModeRef } from '@/types/SwitchContainerModeTypes';
import { apiOrEvent } from '@/utils/integrationServiceUtils';

import { FormEditButton } from '../../components/FormEditButton';
import { VisibilityStates } from './visibilityTypes';

interface props {
  apiType: ApiType;
  apiClassification: ApiClassification;
  organizations: OrganizationVisibility[];
  teams: TeamVisibility[];
  switchContainerModeRef: SwitchContainerModeRef;
  isTeamAdmin: boolean;
  isPublic: boolean;
  setErrorMessage: (value: string | null) => void;
}

export function ViewContainer({
  apiType,
  apiClassification,
  organizations,
  teams,
  switchContainerModeRef,
  isTeamAdmin,
  isPublic,
  setErrorMessage,
}: props) {
  const orgsExist = organizations.length > 0;
  const teamsExist = teams.length > 0;

  const textToShow: VisibilityStates =
    orgsExist && teamsExist
      ? VisibilityStates.BOTHVISIBLE
      : orgsExist
        ? VisibilityStates.ORGANISATIONS
        : teamsExist
          ? VisibilityStates.TEAMS
          : VisibilityStates.NONE;

  return (
    <Form.Section.ViewContainer
      onEdit={() => {
        setErrorMessage(null);
      }}
    >
      <Form.MainHeading>Visibility Settings</Form.MainHeading>

      {isPublic ? (
        <Section innerSpace={{ top: 'small', bottom: 'small' }} top="small" variant="info">
          <P>This {apiOrEvent(apiType)} is public and available to everyone.</P>
        </Section>
      ) : (
        <div>
          {apiClassification === 'public' && apiType === ApiType.REST && (
            <FormStatus
              bottom="small"
              state="warn"
              stretch
              text="This API is classified as public, but is not publicly visible. Contact the Portal team to enable public visibility."
            />
          )}
          {orgsExist || teamsExist ? (
            <>
              <P>{textToShow}</P>
              {orgsExist && (
                <>
                  <Form.SubHeading bottom="small">Organizations</Form.SubHeading>
                  <Table outline>
                    <thead>
                      <Tr>
                        <Th>Name</Th>
                      </Tr>
                    </thead>
                    <tbody>
                      {organizations.map((item) => (
                        <Tr key={item.id}>
                          <Td>{item.name}</Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
              {teamsExist && (
                <>
                  <Form.SubHeading bottom="small">Teams</Form.SubHeading>
                  <Table outline>
                    <thead>
                      <Tr>
                        <Th>Name</Th>
                      </Tr>
                    </thead>
                    <tbody>
                      {teams.map((item) => (
                        <Tr key={item.id}>
                          <Td>{item.name}</Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </>
          ) : (
            <Section innerSpace={{ top: 'small', bottom: 'small' }} top="small" variant="warning">
              <P>{VisibilityStates.NONE}</P>
            </Section>
          )}
        </div>
      )}
      <Form.Section.Toolbar>
        <FormEditButton
          disabled={isPublic || !isTeamAdmin}
          disabledTooltip={
            isPublic ? (
              <div style={{ maxWidth: '30rem', padding: '1rem' }}>
                <P>
                  This {apiOrEvent(apiType)} is public and accessible to everyone. To restrict access, please contact
                  the Developer Portal team to make it not public first - then you can assign access to specific
                  organizations or teams.
                </P>
              </div>
            ) : (
              <P>Only team admins have access to edit</P>
            )
          }
          onEdit={() => setErrorMessage(null)}
          switchContainerModeRef={switchContainerModeRef}
        />
      </Form.Section.Toolbar>
    </Form.Section.ViewContainer>
  );
}
